// src/utils/authSession.js
// Central place for auth storage and the Remember Me policy.
//   Remember Me ON  → access_token lives in localStorage (survives browser restarts).
//   Remember Me OFF → access_token lives in sessionStorage (dies with the browser session).
// remember_me and the remembered email are user preferences, not session data, so they
// survive logout / token invalidation. The password is NEVER written to web storage:
// it is handed to the browser's password manager via the Credential Management API.

const TOKEN_KEY = "access_token";
const SESSION_FLAG_KEY = "auth_session_active";
const REMEMBER_KEY = "remember_me";
const REMEMBERED_EMAIL_KEY = "remembered_email";

// Data tied to the signed-in user; removed on logout so the next user starts clean.
// UI preferences (theme, sidebar layout, …) are intentionally left alone.
const USER_SCOPED_KEYS = [
  TOKEN_KEY,
  "user",
  "token_expiry",
  "selectedContract",
  "cb_watched_report_jobs",
];

const supportsPasswordCredential = () =>
  typeof window !== "undefined" &&
  "PasswordCredential" in window &&
  !!navigator.credentials;

// ── Token ────────────────────────────────────────────────────────────────────
export const getAccessToken = () =>
  sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);

// ── Remember Me preference ───────────────────────────────────────────────────
export const isRememberMeEnabled = () => localStorage.getItem(REMEMBER_KEY) === "true";

export const setRememberPreference = (enabled) => {
  localStorage.setItem(REMEMBER_KEY, enabled ? "true" : "false");
  if (!enabled) forgetCredentials();
};

// ── Saved login information ──────────────────────────────────────────────────
export const getRememberedEmail = () =>
  isRememberMeEnabled() ? localStorage.getItem(REMEMBERED_EMAIL_KEY) || "" : "";

// Password comes from the browser's password manager, never from web storage.
// Resolves to "" when unsupported, not saved, or the user has to pick an account.
export const getRememberedPassword = async (email) => {
  if (!isRememberMeEnabled() || !email || !supportsPasswordCredential()) return "";
  try {
    const cred = await navigator.credentials.get({ password: true, mediation: "silent" });
    return cred?.type === "password" && cred.id === email ? cred.password : "";
  } catch {
    return "";
  }
};

export const rememberCredentials = (email, password) => {
  localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
  if (!supportsPasswordCredential()) return;
  try {
    const cred = new window.PasswordCredential({ id: email, password, name: email });
    navigator.credentials.store(cred).catch(() => {});
  } catch {
    // Non-critical — browser autofill still works via autocomplete attributes
  }
};

export const forgetCredentials = () => {
  localStorage.removeItem(REMEMBERED_EMAIL_KEY);
};

// ── Session lifecycle ────────────────────────────────────────────────────────
export const startSession = (rememberMe, accessToken, credentials) => {
  setRememberPreference(rememberMe);

  if (rememberMe) {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(SESSION_FLAG_KEY);
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (credentials) rememberCredentials(credentials.email, credentials.password);
  } else {
    // Never leave a persistent token behind when Remember Me is off
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.setItem(TOKEN_KEY, accessToken);
    sessionStorage.setItem(SESSION_FLAG_KEY, "true");
  }
};

export const enforceSessionPolicy = () => {
  const remember = localStorage.getItem(REMEMBER_KEY);

  // Sessions created before this policy existed: keep them logged in as they were
  if (remember === null) {
    if (localStorage.getItem(TOKEN_KEY)) localStorage.setItem(REMEMBER_KEY, "true");
    return;
  }

  if (remember === "true") {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(SESSION_FLAG_KEY);
    return;
  }

  // Remember Me OFF: drop any stale persistent token and saved login info
  localStorage.removeItem(TOKEN_KEY);
  forgetCredentials();
  if (!sessionStorage.getItem(SESSION_FLAG_KEY)) {
    sessionStorage.removeItem(TOKEN_KEY);
  }
};

// Logout, 401 / missing token, failed login. Keeps remember_me + remembered email.
export const clearSession = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(SESSION_FLAG_KEY);
  USER_SCOPED_KEYS.forEach((key) => localStorage.removeItem(key));
};
